import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../ui/Button';
import { Dialog } from './accounts/AccountBits';
import nurseryService from '../../services/nursery';
import { errorMessage } from '../../utils/apiResponse';
import { money } from '../../utils/nurseryMoney';
import type { OrderReprice, OrderRepriceLine, OrderRepriceResult } from '../../types/nursery';

/**
 * Update prices: lines entered before their tree had a price take today's
 * price from the book. Lines whose price differs from the book are listed
 * and left alone unless ticked, since a price typed on the order may be
 * deliberate. The server judges it again on apply, and writes a draft
 * invoice's lines again to match.
 */
export function RepriceOrderDialog({ orderId, orderNumber, onClose, onDone }: {
  orderId: number;
  orderNumber: string;
  onClose: () => void;
  onDone: (result: OrderRepriceResult) => void;
}) {
  const { t } = useTranslation('nursery', { keyPrefix: 'RepriceOrderDialog' });
  const [check, setCheck] = useState<OrderReprice | null>(null);
  const [all, setAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    nurseryService.getOrderReprice(orderId)
      .then((res) => (res.success ? setCheck(res.data) : setError(errorMessage(res, t('readFailed')))))
      .catch((e) => setError(errorMessage(e, t('readFailed'))));
  }, [orderId, t]);

  const handleApply = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryService.repriceOrder(orderId, all);
      if (res.success) onDone(res.data);
      else setError(errorMessage(res, t('updateFailed')));
    } catch (e) {
      setError(errorMessage(e, t('updateFailed')));
    }
    setBusy(false);
  };

  const shown = (check?.lines ?? []).filter((l) => l.change !== 'same');
  const willChange = check ? check.to_fill + (all ? check.to_change : 0) : 0;

  return (
    <Dialog
      title={t('title', { number: orderNumber })}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>{t('close')}</Button>
          {check?.can_reprice && willChange > 0 && (
            <Button onClick={handleApply} disabled={busy}>
              {busy ? t('updating') : t('update', { count: willChange })}
            </Button>
          )}
        </>
      }
    >
      {!check && !error && <p className="text-sm text-neutral-500">{t('checking')}</p>}

      {check && !check.can_reprice && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 break-words">
          {check.blocker}
        </div>
      )}

      {check?.can_reprice && shown.length === 0 && (
        <p className="text-sm text-neutral-700">{t('nothing')}</p>
      )}

      {check?.can_reprice && shown.length > 0 && (
        <>
          <ul className="divide-y divide-neutral-100 border border-neutral-200 rounded-lg">
            {shown.map((l) => <RepriceRow key={l.line_id} line={l} all={all} />)}
          </ul>

          {check.to_change > 0 && (
            <label className="flex items-start gap-2 text-sm text-neutral-700">
              <input type="checkbox" className="mt-0.5" checked={all} onChange={(e) => setAll(e.target.checked)} />
              <span className="break-words">
                {t('alsoChange', { count: check.to_change })}
              </span>
            </label>
          )}

          {check.still_unpriced > 0 && (
            <p className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-900 break-words">
              {t('stillUnpriced', { count: check.still_unpriced })}
            </p>
          )}

          {check.invoice && willChange > 0 && (
            <p className="text-xs text-neutral-500 break-words">
              {t('draftInvoice', { number: check.invoice.invoice_number })}
            </p>
          )}
        </>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 break-words">{error}</div>
      )}
    </Dialog>
  );
}

function RepriceRow({ line, all }: { line: OrderRepriceLine; all: boolean }) {
  const { t } = useTranslation('nursery', { keyPrefix: 'RepriceOrderDialog' });
  const moving = line.change === 'fill' || (all && line.change === 'change');
  return (
    <li className="px-3 py-2 text-sm">
      <p className="font-medium text-neutral-800 break-words">{line.description}</p>
      <p className="text-neutral-600 break-words">
        {t('trees', { count: line.quantity })} ·{' '}
        {line.change === 'no_price' ? (
          <span className="text-amber-700">{t('noPriceAnyList')}</span>
        ) : (
          <>
            <span className={moving ? 'line-through text-neutral-400' : ''}>
              {line.unit_price == null ? t('noPrice') : money(line.unit_price)}
            </span>
            {' → '}
            <span className={moving ? 'font-semibold text-neutral-900' : 'text-neutral-500'}>
              {money(line.proposed_price)}
            </span>
            {line.price_list_name && <span className="text-neutral-500"> ({line.price_list_name})</span>}
            {!moving && <span className="text-neutral-500">{t('kept')}</span>}
          </>
        )}
      </p>
    </li>
  );
}
